import React from "react";
import { useSelector } from "react-redux";
import { differenceInMinutes } from "date-fns";
import {
    useTheme,
    Box,
    Typography,
    Chip,
    Divider,
    Select,
    MenuItem,
} from "@mui/material";
import ArrowRightAltIcon from "@mui/icons-material/ArrowRightAlt";
import { formatTZDate } from "state/helpers";
import FlexBetween from "components/FlexBetween";


const DEPARTURE_THRESHOLDS = [
    { max: 0,   level: "departed" },
    { max: 60,  level: "critical" },
    { max: 180, level: "warning-high" },
    { max: 360, level: "warning" },
];

const getDepartureLevel = (minutes) => {
    if (minutes == null) return "normal";
    for (const t of DEPARTURE_THRESHOLDS) {
        if (minutes <= t.max) return t.level;
    }
    return "normal";
};

// Same cache pattern as index.jsx — emotion reuses the cached style object, no CSS regeneration per row
const _pulseCache = {};
const getPulse = (color) => {
    if (!_pulseCache[color]) {
        _pulseCache[color] = {
            animation: "dispatcherPulse 2s ease-in-out infinite",
            "@keyframes dispatcherPulse": {
                "0%":   { boxShadow: `0 0 0 0 ${color}` },
                "50%":  { boxShadow: `0 0 0 4px rgba(0,0,0,0)` },
                "100%": { boxShadow: `0 0 0 0 rgba(0,0,0,0)` },
            },
        };
    }
    return _pulseCache[color];
};

// Extracted so React.memo can skip re-render when value/item hasn't changed
const ChecklistItemRow = React.memo(({ item, value, flight, onUpdateStatus }) => (
    <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
        <Typography
            variant="body2"
            color="text.secondary"
            sx={{ minWidth: 80, flexShrink: 0 }}
        >
            {item.code}
        </Typography>
        <Select
            size="small"
            value={value}
            onChange={(e) => onUpdateStatus(flight, item._id, e.target.value)}
            sx={{
                flex: 1,
                fontSize: "0.8rem",
                color:
                    value === "rejected" ? "error.main" :
                    value === "warning"  ? "warning.main" :
                    value === "checked"  ? "success.main" : "inherit",
            }}
        >
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="checked">Checked</MenuItem>
            <MenuItem value="warning">Warning</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
        </Select>
    </Box>
));


const DispatcherChecklist = ({ rows, checklistItems, onUpdateStatus, now }) => {
    const theme = useTheme();
    const timeMode = useSelector((state) => state.timemode);

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            {rows?.map((flight, index) => {
                const flightDate = timeMode === "UTC"
                    ? formatTZDate(flight.flightDate, "UTC", "dd/MM/yy")
                    : formatTZDate(flight.flightDateLocalBase, "UTC", "dd/MM/yy");

                const etd = timeMode === "UTC" ? flight.etd : flight.etdLocalBase;
                const eta = timeMode === "UTC" ? flight.eta : flight.etaLocalBase;

                const diffMinutes = flight.fullEtd
                    ? differenceInMinutes(new Date(flight.fullEtd), now)
                    : null;
                const level = getDepartureLevel(diffMinutes);
                const isDone    = flight.overallStatus === "done";
                const isPending = !isDone && level !== "departed";

                const borderColor =
                    isDone                   ? theme.palette.success.main :
                    level === "critical"     ? theme.palette.error.main :
                    level === "warning-high" ? theme.palette.warning.dark :
                    level === "warning"      ? theme.palette.warning.main :
                                               theme.palette.neutral.dark;

                const shouldPulse = isPending && ["critical", "warning-high", "warning"].includes(level);

                const overallColor =
                    isDone                   ? "success" :
                    level === "critical"     ? "error"   :
                    level === "warning-high" ? "warning" :
                    level === "warning"      ? "warning" : "default";

                return (
                    <Box
                        key={flight.id || flight._id || index}
                        sx={{
                            p: 1.5,
                            borderRadius: 2,
                            border: `1px solid ${borderColor}`,
                            boxShadow: 1,
                            backgroundColor: "background.paper",
                            transition: "all 0.2s ease-in-out",
                            ...(shouldPulse ? getPulse(borderColor) : {}),
                        }}
                    >
                        <FlexBetween>
                            <Box display="flex" alignItems="center" gap={1}>
                                <Chip
                                    label={index + 1}
                                    variant="outlined"
                                    color="primary"
                                    size="small"
                                    sx={{ fontSize: "0.7rem", height: 20, "& .MuiChip-label": { px: 0.75 } }}
                                />
                                <Typography variant="body2" fontWeight={600} color={theme.palette.primary.dark}>
                                    {flight.flightDesc || "-"}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {flightDate}
                                </Typography>
                            </Box>
                            <Chip
                                size="small"
                                label={flight.overallStatus || "pending"}
                                color={overallColor}
                                sx={{ fontSize: "0.7rem", height: 20 }}
                            />
                        </FlexBetween>

                        <Box mt={0.5} display="flex" flexWrap="wrap" gap={1.5}>
                            <Typography display="flex" alignItems="center" variant="body2" color="text.primary">
                                {flight.dep} <ArrowRightAltIcon sx={{ fontSize: 16, mx: 0.25 }} /> {flight.arr}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {flight.flightReg} | {flight.acType}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                ETD {etd || "-"} · ETA {eta || "-"}
                            </Typography>
                            {diffMinutes > 0 && (
                                <Typography
                                    variant="caption"
                                    fontWeight={600}
                                    color={
                                        level === "critical"     ? "error.main"   :
                                        level === "warning-high" ? "warning.dark" :
                                                                   "text.secondary"
                                    }
                                >
                                    ⏳ {Math.floor(diffMinutes / 60)}h {diffMinutes % 60}m
                                </Typography>
                            )}
                        </Box>

                        {checklistItems?.length > 0 && (
                            <>
                                <Divider sx={{ my: 1 }} />
                                <Box display="flex" flexDirection="column" gap={0.75}>
                                    {checklistItems.map((item) => {
                                        const action = flight.checklist?.actions?.find((a) => {
                                            const id = typeof a.item === "object" ? a.item._id : a.item;
                                            return String(id) === String(item._id);
                                        });
                                        const value = action?.currentStatus || "pending";
                                        return (
                                            <ChecklistItemRow
                                                key={item._id}
                                                item={item}
                                                value={value}
                                                flight={flight}
                                                onUpdateStatus={onUpdateStatus}
                                            />
                                        );
                                    })}
                                </Box>
                            </>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
};

export default DispatcherChecklist;
